export const PROVISIONAL_AUTO_CLOCKOUT_METHOD = "auto_shift_pending";
export const FINAL_AUTO_CLOCKOUT_METHOD = "auto_shift";

function attendanceDateTime(value) {
  if (!value) return null;
  const parsed = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isProvisionalAutoClockout(record) {
  return record?.clock_out_method === PROVISIONAL_AUTO_CLOCKOUT_METHOD;
}

export function isFinalAutoClockout(record) {
  return record?.clock_out_method === FINAL_AUTO_CLOCKOUT_METHOD;
}

export function canReplaceProvisionalAutoClockout(record, now = new Date()) {
  if (!isProvisionalAutoClockout(record) || !record?.scheduled_end_at) return false;
  const scheduledEnd = attendanceDateTime(record.scheduled_end_at);
  if (!scheduledEnd) return false;
  return now.getTime() < scheduledEnd.getTime() + 6 * 60 * 60 * 1000;
}

export function canClockOutAttendance(record, now = new Date()) {
  if (!record || Number(record.admin_closed || 0)) return false;
  if (!record.clock_out && !record.clock_out_at) return true;
  return canReplaceProvisionalAutoClockout(record, now);
}

export function displayClockOut(record, now = new Date()) {
  if (canReplaceProvisionalAutoClockout(record, now)) return null;
  return record?.clock_out || null;
}

export function findCurrentAttendance(records, userId, today, now = new Date()) {
  const mine = (records || []).filter(record =>
    String(record.user_id || "") === String(userId || "")
  );
  const open = mine
    .filter(record => canClockOutAttendance(record, now))
    .sort((a, b) => {
      const aToday = a.work_date === today ? 1 : 0;
      const bToday = b.work_date === today ? 1 : 0;
      if (aToday !== bToday) return bToday - aToday;
      return String(b.clock_in_at || `${b.work_date} ${b.clock_in || ""}`)
        .localeCompare(String(a.clock_in_at || `${a.work_date} ${a.clock_in || ""}`));
    })[0];
  return open || mine.find(record => record.work_date === today) || null;
}

export function hasBlockingAttendance(records, userId, today, now = new Date()) {
  return (records || []).some(record => {
    if (String(record.user_id || "") !== String(userId || "")) return false;
    if (record.work_date === today) return true;
    if (record.clock_out || record.clock_out_at || Number(record.admin_closed || 0)) return false;
    if (!record.scheduled_end_at) return false;
    const scheduledEnd = new Date(String(record.scheduled_end_at).replace(" ", "T"));
    return !Number.isNaN(scheduledEnd.getTime()) && scheduledEnd >= now;
  });
}

export function isEarlyDeparture(record, now = new Date()) {
  if (!record?.scheduled_end_at) return false;
  const scheduledEnd = new Date(String(record.scheduled_end_at).replace(" ", "T"));
  return !Number.isNaN(scheduledEnd.getTime()) && now < scheduledEnd;
}

export function applyClockOutToRecords(records, response, fallback = {}) {
  return (records || []).map(record =>
    String(record.id) === String(response.id)
      ? {
          ...record,
          clock_out: response.clock_out || fallback.clock_out || record.clock_out,
          clock_out_at: response.clock_out_at || fallback.clock_out_at || record.clock_out_at,
          clock_out_latitude: fallback.latitude ?? record.clock_out_latitude,
          clock_out_longitude: fallback.longitude ?? record.clock_out_longitude,
          clock_out_accuracy: fallback.accuracy ?? record.clock_out_accuracy,
          clock_out_method: fallback.method || record.clock_out_method,
          payroll_base_end_at: response.payroll_base_end_at ?? record.payroll_base_end_at,
          payroll_break_minutes: response.payroll_break_minutes ?? record.payroll_break_minutes,
          early_departure_confirmed_at: response.early_departure_confirmed_at ?? record.early_departure_confirmed_at,
          overtime_candidate_minutes: response.overtime_candidate_minutes ?? record.overtime_candidate_minutes,
          overtime_status: response.overtime_status ?? record.overtime_status,
        }
      : record
  );
}

export function calculatePayrollMinutes(record) {
  if (!record?.payroll_base_start_at || !record?.payroll_base_end_at) {
    return record?.attendance_type ? null : calculateAttendanceMinutes(record);
  }
  const start = new Date(String(record.payroll_base_start_at).replace(" ", "T"));
  const end = new Date(String(record.payroll_base_end_at).replace(" ", "T"));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const gross = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  return Math.max(
    0,
    gross + Number(record.approved_overtime_minutes || 0) - Number(record.payroll_break_minutes || 0)
  );
}

export function calculateAttendanceMinutes(record) {
  if (record?.clock_in_at && record?.clock_out_at) {
    const start = new Date(String(record.clock_in_at).replace(" ", "T"));
    const end = new Date(String(record.clock_out_at).replace(" ", "T"));
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    }
  }
  if (!record?.clock_in || !record?.clock_out) return null;
  const [inHour, inMinute] = record.clock_in.split(":").map(Number);
  const [outHour, outMinute] = record.clock_out.split(":").map(Number);
  let minutes = outHour * 60 + outMinute - (inHour * 60 + inMinute);
  if (minutes < 0) minutes += 24 * 60;
  return minutes;
}
